import {FC, useCallback, useEffect, useRef, useState} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Badge} from 'react-bootstrap'
import {PlusCircle} from 'react-bootstrap-icons'
import {useFriendRequestCount} from '../../contexts/FriendRequestCountContext'
import {NavigationItem, isNavItemActive} from './utils/roleHierarchy'
import styles from './megamenu.module.css'

interface MegaMenuProps {
  items: NavigationItem[]
  /** 'hover' for desktop, 'click' for tablet */
  triggerMode: 'hover' | 'click'
  onClose?: () => void
}

interface MegaMenuPanelProps {
  item: NavigationItem
  onNavigate: (path: string) => void
}

const MegaMenuPanel: FC<MegaMenuPanelProps> = ({item, onNavigate}) => {
  const {t} = useTranslation()
  const location = useLocation()

  const getTranslatedLabel = (navItem: NavigationItem): string => {
    return t(`navigation.megamenu.${navItem.key}`, {defaultValue: navItem.label})
  }

  const handleClick = (e: React.MouseEvent, path: string | undefined): void => {
    e.preventDefault()
    if (path) {
      onNavigate(path)
    }
  }

  // Check if item has categories (3-level structure) or just items (2-level)
  const hasCategories = item.children?.some(child => child.isCategory)

  if (hasCategories) {
    // 3-level structure: render categories with their children
    return (
      <div className={styles.panelContent}>
        {item.children?.map(category => (
          <div key={category.key} className={styles.category}>
            {/* Category header - clickable to overview page */}
            <a
              href={category.path || '#'}
              className={`${styles.categoryHeader} ${category.children?.length ? '' : styles.categoryHeaderStandalone}`}
              onClick={(e) => handleClick(e, category.path)}
            >
              {getTranslatedLabel(category)}
            </a>
            {/* Category items */}
            <ul className={styles.categoryItems}>
              {category.children?.map(subItem => {
                const isActive = subItem.path ? location.pathname === subItem.path : false
                return (
                  <li key={subItem.key}>
                    <a
                      href={subItem.path || '#'}
                      className={`${styles.menuItem} ${isActive ? styles.active : ''}`}
                      onClick={(e) => handleClick(e, subItem.path)}
                    >
                      {getTranslatedLabel(subItem)}
                      {subItem.key === 'createEvent' && <PlusCircle className="ms-1" />}
                    </a>
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </div>
    )
  }

  // 2-level structure: render items directly (no categories)
  return (
    <div className={styles.panelContent}>
      <div className={styles.category}>
        <ul className={styles.categoryItems}>
          {item.children?.map(subItem => {
            const isActive = subItem.path ? location.pathname === subItem.path : false
            return (
              <li key={subItem.key}>
                <a
                  href={subItem.path || '#'}
                  className={`${styles.menuItem} ${isActive ? styles.active : ''}`}
                  onClick={(e) => handleClick(e, subItem.path)}
                >
                  {getTranslatedLabel(subItem)}
                  {subItem.key === 'createEvent' && <PlusCircle className="ms-1" />}
                </a>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}

const MegaMenu: FC<MegaMenuProps> = ({items, triggerMode, onClose}) => {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const {count: friendRequestCount} = useFriendRequestCount()
  const [activeItem, setActiveItem] = useState<NavigationItem | null>(null)
  const [isOverlayVisible, setIsOverlayVisible] = useState(false)
  const [isPanelVisible, setIsPanelVisible] = useState(false)
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const openTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const isHoveringMenuRef = useRef(false)

  const getTranslatedLabel = (navItem: NavigationItem): string => {
    return t(`navigation.megamenu.${navItem.key}`, {defaultValue: navItem.label})
  }

  const clearTimeouts = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current)
      openTimeoutRef.current = null
    }
  }, [])

  const openMenu = useCallback((item: NavigationItem) => {
    clearTimeouts()
    setActiveItem(item)
    // Overlay fades in first
    setIsOverlayVisible(true)
    // Then panel slides down after slight delay
    openTimeoutRef.current = setTimeout(() => {
      setIsPanelVisible(true)
    }, 50)
  }, [clearTimeouts])

  const closeMenu = useCallback((force: boolean = false) => {
    clearTimeouts()
    // If not forcing and still hovering, don't close
    if (!force && isHoveringMenuRef.current) {
      return
    }
    setIsPanelVisible(false)
    // Delay hiding overlay until panel is hidden
    closeTimeoutRef.current = setTimeout(() => {
      if (!isHoveringMenuRef.current || force) {
        setIsOverlayVisible(false)
        setActiveItem(null)
        onClose?.()
      }
    }, 150)
  }, [clearTimeouts, onClose])

  const handleNavItemClick = useCallback((item: NavigationItem, e: React.MouseEvent) => {
    if (item.hasMegamenu) {
      e.preventDefault()
      if (triggerMode === 'click') {
        // Toggle on click for tablet
        if (activeItem?.key === item.key && isPanelVisible) {
          isHoveringMenuRef.current = false
          closeMenu(true)
        } else {
          openMenu(item)
        }
      } else {
        // Desktop hover mode: click navigates to item's own path
        const defaultPath = item.path || item.children?.[0]?.path || '/competitions'
        navigate(defaultPath)
        isHoveringMenuRef.current = false
        closeMenu(true)
      }
    } else if (item.path) {
      // Direct link - navigate
      navigate(item.path)
      isHoveringMenuRef.current = false
      closeMenu(true)
    }
  }, [activeItem, isPanelVisible, triggerMode, openMenu, closeMenu, navigate])

  const handleNavItemMouseEnter = useCallback((item: NavigationItem) => {
    if (triggerMode === 'hover') {
      isHoveringMenuRef.current = true
      clearTimeouts()
      if (item.hasMegamenu) {
        // Slight delay to prevent accidental triggers
        openTimeoutRef.current = setTimeout(() => {
          openMenu(item)
        }, 50)
      } else {
        // Close menu when hovering non-megamenu items
        closeMenu(true)
      }
    }
  }, [triggerMode, openMenu, closeMenu, clearTimeouts])

  const handleNavItemMouseLeave = useCallback(() => {
    if (triggerMode === 'hover') {
      // Don't set isHoveringMenuRef to false here - let container handle it
      // Just schedule a delayed close that will be cancelled if we enter panel
      clearTimeouts()
      closeTimeoutRef.current = setTimeout(() => {
        // Only close if not hovering over panel
        if (!isHoveringMenuRef.current) {
          closeMenu(true)
        }
      }, 200)
    }
  }, [triggerMode, closeMenu, clearTimeouts])

  const handlePanelMouseEnter = useCallback(() => {
    if (triggerMode === 'hover') {
      isHoveringMenuRef.current = true
      clearTimeouts()
    }
  }, [triggerMode, clearTimeouts])

  const handlePanelMouseLeave = useCallback(() => {
    if (triggerMode === 'hover') {
      isHoveringMenuRef.current = false
      closeMenu()
    }
  }, [triggerMode, closeMenu])

  const handleContainerMouseLeave = useCallback(() => {
    if (triggerMode === 'hover') {
      isHoveringMenuRef.current = false
      // Small delay to allow re-entering the menu
      closeTimeoutRef.current = setTimeout(() => {
        if (!isHoveringMenuRef.current) {
          closeMenu(true)
        }
      }, 100)
    }
  }, [triggerMode, closeMenu])

  const handleOverlayClick = useCallback(() => {
    isHoveringMenuRef.current = false
    closeMenu(true)
  }, [closeMenu])

  const handleNavigate = useCallback((path: string) => {
    navigate(path)
    isHoveringMenuRef.current = false
    closeMenu(true)
  }, [closeMenu, navigate])

  // Close on escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isPanelVisible) {
        isHoveringMenuRef.current = false
        closeMenu(true)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isPanelVisible, closeMenu])

  // Close when location changes
  useEffect(() => {
    isHoveringMenuRef.current = false
    // eslint-disable-next-line react-hooks/set-state-in-effect
    closeMenu(true)
  }, [location.pathname]) // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup on unmount
  useEffect(() => {
    return () => clearTimeouts()
  }, [clearTimeouts])

  return (
    <div
      className={`${styles.megaMenuContainer} ${isPanelVisible ? styles.menuOpen : ''}`}
      ref={menuRef}
      onMouseLeave={handleContainerMouseLeave}
    >
      {/* Navigation items */}
      <nav className={styles.navBar}>
        {items.map(item => {
          const isActive = isNavItemActive(item, location.pathname)
          const isOpen = activeItem?.key === item.key && isPanelVisible

          return (
            <div
              key={item.key}
              className={styles.navItemWrapper}
              onMouseEnter={() => handleNavItemMouseEnter(item)}
              onMouseLeave={handleNavItemMouseLeave}
            >
              {item.path && !item.hasMegamenu ? (
                <a
                  href={item.path}
                  className={`${styles.navItem} ${isActive ? styles.active : ''}`}
                  onClick={(e) => {
                    e.preventDefault()
                    navigate(item.path!)
                    closeMenu()
                  }}
                  data-testid={`nav-${item.key}`}
                >
                  {getTranslatedLabel(item)}
                  {item.key === 'friends' && friendRequestCount > 0 && (
                    <Badge
                      bg="danger"
                      className="ms-1 d-inline-flex align-items-center justify-content-center"
                      style={{
                        fontSize: '0.6rem',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        padding: 0
                      }}
                    >
                      {friendRequestCount}
                    </Badge>
                  )}
                </a>
              ) : (
                <button
                  type="button"
                  className={`${styles.navItem} ${isActive ? styles.active : ''} ${isOpen ? styles.open : ''}`}
                  onClick={(e) => handleNavItemClick(item, e)}
                  aria-expanded={isOpen}
                  aria-haspopup="true"
                  data-testid={`nav-${item.key}`}
                >
                  {getTranslatedLabel(item)}
                </button>
              )}
            </div>
          )
        })}
      </nav>

      {/* Overlay */}
      <div
        className={`${styles.overlay} ${isOverlayVisible ? styles.visible : ''}`}
        onClick={handleOverlayClick}
        aria-hidden="true"
      />

      {/* Megamenu panel */}
      {activeItem && activeItem.hasMegamenu && (
        <div
          className={`${styles.panel} ${isPanelVisible ? styles.visible : ''}`}
          onMouseEnter={handlePanelMouseEnter}
          onMouseLeave={handlePanelMouseLeave}
        >
          <MegaMenuPanel item={activeItem} onNavigate={handleNavigate} />
        </div>
      )}
    </div>
  )
}

export default MegaMenu
